import { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import { elementToEditorData } from 'components/WorkspaceEditor/components/ContentEditor/contentUtils'
import { useNewContentFocus } from 'components/WorkspaceEditor/features/scrollToNewContent/hooks/useNewContentFocus'
import {
    useCKEditorToggle,
    useContentEditorFocus,
} from 'components/WorkspaceEditor/hooks'
import { useTransactionManager } from 'components/WorkspaceEditor/services'
import { nullOrUndefinedResolver } from 'components/WorkspaceEditor/utils'
import { useMemo, useState } from 'react'
import { useShallowSelector } from '../../../../Workspace2Store'
import { useNewContentEditing } from '../../../../features/scrollToNewContent'
import type {
    ClassicEditorHandler,
    ClassicEditorOnReadyHandler,
} from '../../../ContentEditor'
import { useKeyedText } from './useKeyedText'
import { usePlainTextContentMutation } from './usePlainTextContentMutation'
import { usePlainTextHandlers } from './usePlainTextHandlers'
import { useSaveAwareEditorActivation } from './useSaveAwareEditorActivation'

/**
 * Defines the structure for CKEditor event handlers used in the plain text controller.
 */
interface PlainTextHandlers {
    /** Handler triggered when the CKEditor instance is ready. Used to store the editor reference for potential rollbacks. */
    onReady: ClassicEditorOnReadyHandler
    /** Handler triggered when the CKEditor instance loses focus. Handles saving changes via optimistic updates. */
    onBlur: ClassicEditorHandler
    /** Debounced handler triggered when the CKEditor content changes. Calculates and stores unsaved changes. */
    onChange: ClassicEditorHandler
    /** Handler triggered when the CKEditor instance gains focus. Handles setting the editor to active state. */
    onFocusCapture: (event?: React.FocusEvent<HTMLElement>) => void
    /** Handler triggered when the text is clicked. Captures click coordinates for cursor positioning. */
    onClick: (event: React.MouseEvent<HTMLElement>) => void
    /** Handler triggered when mouse moves over the text. Tracks cursor position for positioning. */
    onMouseMove: (event: React.MouseEvent<HTMLElement>) => void
    /** Handler triggered when a key is pressed. Uses onKeyDown for Enter/Save, onKeyUp for Escape. */
    onKeyDown: (event: React.KeyboardEvent<HTMLElement>) => void
    /** Handler triggered when a key is released. Optimized for Escape key reliability. */
    onKeyUp: (event: React.KeyboardEvent<HTMLElement>) => void
}

/**
 * Custom hook to manage the state and interactions for a plain text content item
 * using CKEditor within the workspace. It handles editor activation, optimistic updates,
 * and synchronization with the workspace tree state.
 *
 * @param {Partial<TextContentElement>} contentItem - The content item data, potentially incomplete.
 * @returns An object containing state, handlers, and derived values for the plain text editor.
 */
export const usePlainTextController = (
    contentItem: Partial<TextContentElement>
) => {
    const textElement = useMemo(() => nullOrUndefinedResolver(contentItem), [
        contentItem,
    ])
    const [unsavedData, setUnsavedData] = useState<string>('')
    const formattedContentId = useMemo(
        () => `plain-text-${textElement.id || 'default'}`,
        [textElement.id]
    )

    const { isContentTeamFromStudioPage } = useShallowSelector((s) => ({
        isContentTeamFromStudioPage: s.isContentTeamFromStudioPage,
    }))

    const { registerEditorRef, editorRef } = useTransactionManager()
    const [shouldTriggerNewContent, setShouldTriggerNewContent] = useState(
        false
    )
    const [clickCoordinates, setClickCoordinates] = useState<
        { x: number; y: number } | undefined
    >()

    const {
        handleAddContentExecuteOptimisticWithFallback,
        handleContentMutationCompleted,
    } = usePlainTextContentMutation({
        setUnsavedData,
        setShouldTriggerNewContent,
    })

    const {
        keyedText,
        keyedTextValue,
        isKeyedText,
        prependKeyedTextElement,
    } = useKeyedText(contentItem, isContentTeamFromStudioPage)

    const initialData = useMemo(() => {
        if (isKeyedText && keyedTextValue) {
            // for keyed text with value, create an element with the keyedTextValue to convert to editor data
            return elementToEditorData({ ...textElement, text: keyedTextValue })
        }

        return elementToEditorData(textElement)
    }, [textElement, isKeyedText, keyedTextValue])

    const ckeToggle = useCKEditorToggle(formattedContentId)

    // Use the centralized save-aware editor activation hook
    const { handleClickWithSaveSupport } = useSaveAwareEditorActivation({
        ckeToggle,
        setClickCoordinates,
    })

    const shouldRenderContentEditor = useMemo(
        () =>
            ckeToggle.active ||
            (Boolean(unsavedData) && unsavedData !== textElement.text),
        [ckeToggle.active, unsavedData, textElement.text]
    )

    const { editorFocusRef } = useContentEditorFocus({
        contentItemId: contentItem.id,
        activate: ckeToggle.activate,
        controlType: 'plainText',
    })

    const { registerNewEditor, unFocusEditor } = useNewContentFocus({
        contentItemId: contentItem.id,
        activate: ckeToggle.activate,
    })

    const [hasDeletedWholeText, setHasDeletedWholeText] = useState(false)
    const { startEditing } = useNewContentEditing()

    // Extract all handler logic into a single hook
    const {
        handleOnBlur,
        handleOnFocusCapture,
        handleOnClick,
        handleOnMouseMove,
        handleOnKeyDown,
        handleOnKeyUp,
        debouncedOnChange,
        initialSuggestions,
        setIsEditingPlainText,
    } = usePlainTextHandlers({
        textElement,
        contentItem,
        unsavedData,
        hasDeletedWholeText,
        setUnsavedData,
        setHasDeletedWholeText,
        unFocusEditor,
        handleAddContentExecuteOptimisticWithFallback,
        handleContentMutationCompleted,
        prependKeyedTextElement,
        editorRef,
        shouldTriggerNewContent,
        setShouldTriggerNewContent,
        startEditing,
        registerNewEditor,
        registerEditorRef,
        ckeToggle,
        setClickCoordinates,
    })

    // Compose handlers object with enhanced wrapper handler
    const handlers: PlainTextHandlers = useMemo(
        () => ({
            onReady: (editor) => {
                registerNewEditor(editor)
                // register the CKEditor instance to use in Transaction Manager (optimistic update)
                registerEditorRef(editor)
                // register the editor for focus management
                editorFocusRef.current = editor
            },
            onBlur: handleOnBlur,
            onChange: debouncedOnChange,
            onFocusCapture: handleOnFocusCapture,
            onClick: (event) =>
                handleClickWithSaveSupport(event, handleOnClick),
            onMouseMove: handleOnMouseMove,
            onKeyDown: handleOnKeyDown,
            onKeyUp: handleOnKeyUp,
        }),
        [
            registerNewEditor,
            registerEditorRef,
            editorFocusRef,
            handleOnBlur,
            debouncedOnChange,
            handleOnFocusCapture,
            handleClickWithSaveSupport,
            handleOnClick,
            handleOnMouseMove,
            handleOnKeyDown,
            handleOnKeyUp,
        ]
    )

    return {
        text: textElement.text,
        formattedContentId,
        initialData,
        initialSuggestions,
        clickCoordinates,
        ckeToggle,
        shouldRenderContentEditor,
        isKeyedText,
        keyedText,
        keyedTextValue,
        handlers,
        setIsEditingPlainText,
    }
}
